import { useState } from "react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useLang } from "@/i18n";

const ease = [0.4, 0, 0.2, 1];

const contactIcons = [
  "mdi:map-marker-outline",
  "mdi:map-marker-outline",
  "mdi:email-outline",
  "mdi:phone-outline",
  "mdi:message-text-outline",
  "mdi:wechat",
  "mdi:instagram",
];
const contactFlags = [
  { isPhone: false, isLink: false },
  { isPhone: false, isLink: false },
  { isPhone: false, isLink: false },
  { isPhone: true, isLink: false },
  { isPhone: false, isLink: false },
  { isPhone: false, isLink: false },
  { isPhone: false, isLink: true, href: "https://www.instagram.com/ascend_marketingagency/" },
];

export default function Contact() {
  const { t } = useLang();
  const contactItems = t.contact.items.map((item, i: number) => ({
    ...item,
    icon: contactIcons[i],
    ...contactFlags[i],
  }));
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);

  const inputStyle = (field: string) => ({
    width: "100%",
    background: "none",
    border: "none",
    borderBottom: `1px solid ${focused === field ? "#f4440c" : "rgba(255,255,255,0.18)"}`,
    color: "#fff",
    fontSize: "0.98rem",
    fontWeight: 400,
    padding: "0.75rem 0",
    outline: "none",
    transition: "border-color 0.3s",
    letterSpacing: "0.02em",
  });

  const labelStyle = {
    display: "block",
    fontSize: "0.72rem",
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    color: "rgba(255,255,255,0.45)",
    fontWeight: 400,
    marginBottom: "0.25rem",
  };

  return (
    <section id="contact" style={{ background: "#050505", overflow: "hidden" }}>
      {/* Top accent */}
      <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(244,68,12,0.5) 50%, transparent)" }} />
      <div className="contact-grid">

        {/* Left: info panel */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="contact-left-pad"
        >
          <div>
            {/* Label */}
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "2rem" }}>
              <div style={{ width: 32, height: 1, background: "#f4440c" }} />
              <span style={{ fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f4440c", fontWeight: 400 }}>
              {t.contact.eyebrow}
              </span>
            </div>

            <h2 style={{
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: "clamp(2.8rem, 5vw, 4.8rem)",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 0.95,
              letterSpacing: "-0.04em",
              marginBottom: "1.5rem",
            }}>
              {t.contact.headline1}<br />
              <span style={{ color: "transparent", WebkitTextStroke: "2px #f4440c" }}>{t.contact.headline2}</span>
            </h2>

            <p style={{
              fontSize: "0.98rem",
              lineHeight: 1.9,
              color: "rgba(255,255,255,0.6)",
              fontWeight: 400,
              maxWidth: 340,
              marginBottom: "3rem",
            }}>
              {t.contact.sub}
            </p>

            {/* Contact list */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1.4rem" }}>
              {contactItems.map((item: any) => (
                <div key={item.label} style={{ display: "flex", alignItems: "flex-start", gap: "1.25rem" }}>
                  <Icon icon={item.icon} style={{ fontSize: 15, color: "#f4440c", marginTop: "0.2rem", flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: "0.2rem" }}>
                      {item.label}
                    </div>
                    {item.isLink ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.92rem", color: "#f4440c", fontWeight: 400, lineHeight: 1.7, textDecoration: "none" }}>
                        {item.value}
                      </a>
                    ) : item.isPhone ? (
                      <a href={`tel:${item.value.replace(/\s/g, "")}`} style={{ fontSize: "0.92rem", color: "rgba(255,255,255,0.78)", fontWeight: 400, lineHeight: 1.7, textDecoration: "none" }}>
                        {item.value}
                      </a>
                    ) : (
                      <div style={{ fontSize: "0.92rem", color: "rgba(255,255,255,0.78)", fontWeight: 400, whiteSpace: "pre-line", lineHeight: 1.7 }}>
                        {item.value}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom decoration */}
          <div style={{
            marginTop: "3rem",
            paddingTop: "1.75rem",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            fontSize: "0.65rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.15)",
          }}>
            Ascend Marketing Agency · Est. 2022
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15, ease }}
          className="contact-right-pad"
          style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
        >
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ textAlign: "center", padding: "4rem 2rem" }}
            >
              <div style={{
                width: 56,
                height: 56,
                border: "1px solid #f4440c",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 2rem",
              }}>
                <Icon icon="mdi:check" style={{ fontSize: 24, color: "#f4440c" }} />
              </div>
              <h3 style={{
                fontFamily: "'Inter Tight', sans-serif",
                fontSize: "2rem",
                fontWeight: 300,
                color: "#fff",
                marginBottom: "0.75rem",
              }}>
                {t.contact.sent}
              </h3>
              <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)", lineHeight: 1.9, fontWeight: 400 }}>
                {t.contact.sentBody}
              </p>
            </motion.div>
          ) : (
            <form onSubmit={(e) => {
              e.preventDefault();
              const subject = encodeURIComponent(t.contact.emailSubject.replace("{name}", form.company || form.name));
              const body = encodeURIComponent(`${t.contact.fieldName}: ${form.name}\n${t.contact.fieldCompany}: ${form.company}\n${t.contact.fieldEmail}: ${form.email}\n\n${t.contact.fieldMessage}:\n${form.message}`);
              window.location.href = `mailto:sylvia@chinaascend.net?subject=${subject}&body=${body}`;
              setSubmitted(true);
            }}>
              <div style={{ marginBottom: "3rem" }}>
                <h3 style={{
                  fontFamily: "'Inter Tight', sans-serif",
                  fontSize: "1.8rem",
                  fontWeight: 300,
                  color: "#fff",
                  marginBottom: "0.5rem",
                }}>
                  {t.contact.formTitle}
                </h3>
                <p style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.3)", fontWeight: 400, letterSpacing: "0.04em" }}>
                  {t.contact.formSub}
                </p>
              </div>

              {/* Fields */}
              <div className="contact-form-grid">
                <div>
                  <label style={labelStyle}>{t.contact.fieldName} *</label>
                  <input
                    type="text" required value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    onFocus={() => setFocused("name")}
                    onBlur={() => setFocused(null)}
                    placeholder="Your name"
                    style={inputStyle("name")}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t.contact.fieldCompany}</label>
                  <input
                    type="text" value={form.company}
                    onChange={e => setForm({ ...form, company: e.target.value })}
                    onFocus={() => setFocused("company")}
                    onBlur={() => setFocused(null)}
                    placeholder="Company"
                    style={inputStyle("company")}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "2rem" }}>
                <label style={labelStyle}>{t.contact.fieldEmail} *</label>
                <input
                  type="email" required value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  onFocus={() => setFocused("email")}
                  onBlur={() => setFocused(null)}
                  placeholder="your@email.com"
                  style={inputStyle("email")}
                />
              </div>

              <div style={{ marginBottom: "3rem" }}>
                <label style={labelStyle}>{t.contact.fieldMessage} *</label>
                <textarea
                  required rows={4} value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  onFocus={() => setFocused("message")}
                  onBlur={() => setFocused(null)}
                  placeholder={t.contact.fieldMessagePlaceholder}
                  style={{ ...inputStyle("message"), resize: "none", lineHeight: 1.8 }}
                />
              </div>

              <button
                type="submit"
                style={{
                  background: "#f4440c",
                  color: "#fff",
                  border: "none",
                  padding: "1rem 2.5rem",
                  fontSize: "0.92rem",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  transition: "background 0.3s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#d93a0a"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "#f4440c"; }}
              >
                {t.contact.send}
                <Icon icon="mdi:send" style={{ fontSize: 15 }} />
              </button>

            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}